import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  model,
  viewChild,
} from '@angular/core';
import { ControlContainer, FormsModule, NgModelGroup } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BREAKPOINTS } from '@lassi/webapp-shared';
import { DateFormatValidatorDirective } from './date-format-validator';

class DateGroupErrorStateMatcher implements ErrorStateMatcher {
  constructor(private readonly dateGroup: NgModelGroup) {}

  isErrorState(): boolean {
    return !!this.dateGroup.invalid && !!this.dateGroup.touched;
  }
}

@Component({
  selector: 'lib-dropdown-date-form-field-component',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    DateFormatValidatorDirective,
  ],
  template: `
    <div class="full-width container">
      <div class="label-container">
        <mat-label class="mat-title-small title">
          {{ label() }}{{ required() ? ' *' : '' }}
        </mat-label>
        @if (tooltipText()) {
          <mat-icon class="tooltip-icon" matSuffix [matTooltip]="tooltipText()">
            info_outline
          </mat-icon>
        }
      </div>

      <div
        ngModelGroup="date"
        #dateGroup="ngModelGroup"
        class="date-inputs-container"
        libDateFormatValidator
        [dayField]="dayFieldName()"
        [monthField]="monthFieldName()"
        [yearField]="yearFieldName()"
        [required]="required()"
      >
        <mat-form-field appearance="outline" class="field">
          <mat-label>Day</mat-label>
          <mat-select
            [name]="dayFieldName()"
            [(ngModel)]="day"
            [errorStateMatcher]="errorMatcher"
          >
            <mat-option [value]="null"></mat-option>
            @for (day of days; track day) {
              <mat-option [value]="day">
                {{ day }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="field">
          <mat-label>Month</mat-label>
          <mat-select
            [name]="monthFieldName()"
            [(ngModel)]="month"
            [errorStateMatcher]="errorMatcher"
          >
            <mat-option [value]="null"></mat-option>
            @for (month of months; track month) {
              <mat-option [value]="month">
                {{ month }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="field" err>
          <mat-label>Year</mat-label>
          <input
            matInput
            #yearField="ngModel"
            type="number"
            pattern="^[0-9]{4}$"
            [name]="yearFieldName()"
            [(ngModel)]="year"
            inputmode="numeric"
            [errorStateMatcher]="errorMatcher"
          />
          @if (yearField.getError('pattern')) {
            <mat-error>YYYY</mat-error>
          }
        </mat-form-field>
      </div>

      @if (hint()) {
        <mat-hint>{{ hint() }}</mat-hint>
      }

      @if (required() && dateGroup.hasError('required') && dateGroup.touched) {
        <mat-error> Date is required </mat-error>
      } @else if (
        dateGroup.hasError('invalidDateFormat') && dateGroup.touched
      ) {
        <mat-error>
          Please enter either year, month + year or day + month + year
        </mat-error>
      }
    </div>
  `,
  styles: `
    @use 'breakpoints' as *;

    .container {
      margin-bottom: 1rem;
    }

    .label-container {
      display: flex;
      align-items: center;
      padding-bottom: 0.5rem;
      gap: 0.5rem;

      .title {
        font-weight: bold;
        display: inline-block;
      }

      .tooltip-icon {
        flex-shrink: 0;
      }
    }

    .date-inputs-container {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 8px;

      .field {
        flex: 1 1 100%;
        width: 100%;
      }

      @include web {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
})
export class DropdownDateFormFieldComponent {
  isWeb = inject(BREAKPOINTS).isWeb;
  readonly dateGroupDirective = viewChild.required<NgModelGroup>('dateGroup');
  errorMatcher!: ErrorStateMatcher;

  label = input<string>('Date');
  tooltipText = input<string>('');
  hint = input<string>('');

  dayFieldName = input<string>('day');
  monthFieldName = input<string>('month');
  yearFieldName = input<string>('year');

  day = model<number | null>();
  month = model<number | null>();
  year = model<number | null>();
  required = input<boolean>(false);

  days: number[] = Array.from({ length: 31 }, (_, i) => i + 1);
  months: number[] = Array.from({ length: 12 }, (_, i) => i + 1);

  constructor() {
    afterRenderEffect({
      read: () => {
        this.errorMatcher = new DateGroupErrorStateMatcher(
          this.dateGroupDirective(),
        );
      },
    });
  }
}
